import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ConfirmDialog, NotFoundState, showToast } from '@ideoz/ui';
import {
  useAppDispatch,
  useAppSelector,
  useStartups,
  useScopeConversations,
  useInvalidateScopeConversations,
  selectConversationId,
  resetConversation,
  setMode,
  setCurrentProjectId,
  setCurrentScopeId,
  projectService,
  type ScopeDto,
} from '@ideoz/core';
import ConversationPage from './ConversationPage';

/**
 * Scope conversation workspace — `/:startupSlug/:scopeId/:conversationId`.
 *
 * The conversation id lives in the **path** and is the single source of truth
 * for which chat is open (the old `?chat=` query + its URL-sync/reset effects
 * are gone). A reserved `:conversationId` value of `new` is the explicit
 * new-chat route: it fires exactly one `auto_prompt` greeting, then replaces the
 * URL with the created conversation's id.
 *
 * Entry (`/:slug/:scopeId`) is handled by `ScopeConversationResolver`, which
 * redirects here — so this component always mounts with a concrete id or `new`,
 * never an indeterminate empty state.
 */
export default function ScopeConversationPage(): JSX.Element {
  const { scopeId, startupSlug, conversationId } = useParams<{
    scopeId: string;
    startupSlug: string;
    conversationId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const {
    data: startups = [],
    isFetched: startupsFetched,
    isFetching: startupsFetching,
    isError: startupsError,
  } = useStartups();
  const matchedStartup = startups.find((s) => s.slug === startupSlug) ?? null;
  const startupId = matchedStartup?.id ?? null;

  const isNewChat = conversationId === 'new';
  const activeConversationId = isNewChat ? null : (conversationId ?? null);
  const reduxConversationId = useAppSelector(selectConversationId);

  const [scope, setScope] = useState<ScopeDto | null>(null);
  // Recent chats — shared React-Query cache keyed by scope (reused across the
  // scope's sub-pages). Only feeds the sidebar list now; the greeting no longer
  // depends on it.
  const {
    data: convData,
    isLoading: isLoadingConversations,
  } = useScopeConversations(startupId, scopeId);
  const conversations = convData ?? [];

  // The Strategy / Clarify pills were removed — the AI auto-selects. The FE only
  // distinguishes `prototyper` (composer-triggered) from the default `clarify`;
  // a legacy `?mode=strategy` collapses to `clarify`.
  const rawMode = searchParams.get('mode') as 'clarify' | 'prototyper' | 'strategy' | null;
  const sidebarMode: 'clarify' | 'prototyper' = rawMode === 'prototyper' ? 'prototyper' : 'clarify';
  const activeMode: 'clarify' | 'prototyper' | 'strategy' = sidebarMode;

  // Conversations live under the startup's project; pass scopeId so the
  // streaming call writes conversations.scopeId and the recent-chats query can
  // filter by scope.
  useEffect(() => {
    if (startupId) dispatch(setCurrentProjectId(startupId));
    if (scopeId) dispatch(setCurrentScopeId(scopeId));
    return () => {
      dispatch(setCurrentProjectId(null));
      dispatch(setCurrentScopeId(null));
    };
  }, [dispatch, startupId, scopeId]);

  /** Keep Redux mode aligned with the URL. */
  useEffect(() => {
    dispatch(setMode(activeMode));
  }, [dispatch, activeMode]);

  const invalidateScopeConversations = useInvalidateScopeConversations();
  // The conversation id we entered the new-chat route with, so the redirect
  // below never fires against the *previous* chat's stale id (the bounce bug).
  const newChatBaselineRef = useRef<string | null>(null);

  // Entering `…/new` → remember the id we came from and clear it so the greeting
  // streams into a fresh conversation. `reduxConversationId` is read at the flip
  // moment on purpose — making it a dep would re-run this and wipe the newly
  // created conversation. Switching between existing chats is handled by the
  // facade's own load effect, not here.
  useEffect(() => {
    if (isNewChat) {
      newChatBaselineRef.current = reduxConversationId ?? null;
      dispatch(resetConversation());
    } else {
      newChatBaselineRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- capture on new-chat entry only
  }, [isNewChat, dispatch]);

  // Once the greeting / first message creates a *new* conversation (id differs
  // from the one we entered with), move the URL from `…/new` to `…/:createdId`
  // and refresh the recent-chats list so the sidebar adds the new item.
  useEffect(() => {
    if (!isNewChat || !reduxConversationId || !startupSlug || !scopeId) return;
    if (reduxConversationId === newChatBaselineRef.current) return; // stale entry id
    void invalidateScopeConversations(startupId, scopeId);
    navigate(`/${startupSlug}/${scopeId}/${reduxConversationId}`, { replace: true });
  }, [
    isNewChat,
    reduxConversationId,
    startupSlug,
    scopeId,
    startupId,
    navigate,
    invalidateScopeConversations,
  ]);

  // Scope metadata (name for the header). Loaded once per scope.
  const loadedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!startupId || !scopeId) return;
    const key = `${startupId}::${scopeId}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    projectService
      .getScopeById(startupId, scopeId)
      .then(setScope)
      .catch((err) => console.error('Failed to load scope:', err));
  }, [startupId, scopeId]);

  const handleScopeModeChange = useCallback(
    (mode: 'clarify' | 'prototyper' | 'strategy') => {
      if (mode === sidebarMode || !startupSlug || !scopeId) return;
      // Mode switch starts a fresh chat in that mode (the new-chat route).
      const q = mode === 'clarify' ? '' : `?mode=${mode}`;
      navigate(`/${startupSlug}/${scopeId}/new${q}`);
    },
    [sidebarMode, startupSlug, scopeId, navigate]
  );

  const handleSelectConversation = useCallback(
    (id: string) => {
      if (startupSlug && scopeId) navigate(`/${startupSlug}/${scopeId}/${id}`);
    },
    [startupSlug, scopeId, navigate]
  );

  const handleCreateConversation = useCallback(() => {
    if (startupSlug && scopeId) navigate(`/${startupSlug}/${scopeId}/new`);
  }, [startupSlug, scopeId, navigate]);

  // ---- Delete conversation (from the sidebar recent-chats list) ----
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRequestDeleteConversation = useCallback(
    (id: string) => {
      const title = conversations.find((c) => c.id === id)?.title ?? '';
      setPendingDelete({ id, title });
    },
    [conversations]
  );

  const handleConfirmDeleteConversation = useCallback(async () => {
    if (!pendingDelete || !startupId) return;
    setIsDeleting(true);
    try {
      await projectService.deleteConversation(startupId, pendingDelete.id);
      await invalidateScopeConversations(startupId, scopeId);
      showToast({ message: t('conversationDelete.deleted'), severity: 'success' });
      // If the open conversation was the one deleted, re-resolve the scope so it
      // opens the next remaining chat (or a fresh one) instead of a dead id.
      if (pendingDelete.id === activeConversationId && startupSlug && scopeId) {
        navigate(`/${startupSlug}/${scopeId}`, { replace: true });
      }
      setPendingDelete(null);
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      showToast({ message: t('conversationDelete.deleteFailed'), severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [
    pendingDelete,
    startupId,
    scopeId,
    startupSlug,
    activeConversationId,
    invalidateScopeConversations,
    navigate,
    t,
  ]);

  // 404 for a bad slug — only after the startups query settles.
  if (startupsFetched && !startupsFetching && !startupsError && (!matchedStartup || !startupId)) {
    return (
      <NotFoundState
        description="Startup not found"
        actionLabel="Back to My Startups"
        onAction={() => navigate('/startups')}
      />
    );
  }

  return (
    <>
    <ConversationPage
      sidebarPageType="scope"
      urlConversationIdOverride={activeConversationId}
      routeProjectId={startupId}
      scopeModeControl={{
        activeMode: sidebarMode,
        onModeChange: handleScopeModeChange,
        conversations: conversations.map((c) => ({ id: c.id, title: c.title })),
        isLoadingConversations,
        activeConversationId,
        isNewChat,
        onSelectConversation: handleSelectConversation,
        onCreateConversation: handleCreateConversation,
        onDeleteConversation: handleRequestDeleteConversation,
        // Back always returns to the scope's Work Scopes list.
        onBack: () => navigate(startupSlug ? `/${startupSlug}/work-scopes` : '/startup'),
        // Bad/forbidden chat → re-resolve the scope (opens last or new).
        onConversationNotFound: () => {
          if (startupSlug && scopeId) navigate(`/${startupSlug}/${scopeId}`, { replace: true });
        },
        scopeTitle: scope?.name,
        onOpenFiles:
          startupSlug && scopeId
            ? () => navigate(`/${startupSlug}/${scopeId}/files`)
            : undefined,
        onOpenArtifacts:
          startupSlug && scopeId
            ? () => navigate(`/${startupSlug}/${scopeId}/artifacts`)
            : undefined,
      }}
    />
    <ConfirmDialog
      open={!!pendingDelete}
      variant="danger"
      title={t('conversationDelete.title')}
      description={
        pendingDelete?.title
          ? t('conversationDelete.desc', { title: pendingDelete.title })
          : t('conversationDelete.descUntitled')
      }
      confirmLabel={t('conversationDelete.confirm')}
      cancelLabel={t('conversationDelete.cancel')}
      isLoading={isDeleting}
      onConfirm={handleConfirmDeleteConversation}
      onCancel={() => setPendingDelete(null)}
    />
    </>
  );
}
